import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import i18n from '../../i18n';
import { Globe, ChevronDown } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '/flags/us.png' },
  { code: 'es', name: 'Español', flag: '/flags/es.png' },
  { code: 'pt', name: 'Português', flag: '/flags/pt.png' },
  { code: 'fr', name: 'Français', flag: '/flags/fr.png' },
  { code: 'de', name: 'Deutsch', flag: '/flags/de.png' },
  { code: 'it', name: 'Italiano', flag: '/flags/it.png' },
  { code: 'ja', name: '日本語', flag: '/flags/jp.png' },
];

const LanguageSwitcher = () => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLang] = useState(i18n.language?.split('-')[0] || 'en');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handle = (lng) => setLang(lng.split('-')[0]);
    i18n.on('languageChanged', handle);
    return () => i18n.off('languageChanged', handle);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLanguage = languages.find(l => l.code === lang) || languages[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLang(code);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <div
        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
          isDarkMode
            ? 'bg-gray-800 border-gray-600 hover:bg-gray-700'
            : 'bg-white border-gray-300 hover:bg-gray-50'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <img src={currentLanguage.flag} alt="" className="w-4 h-3" />
        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {currentLanguage.name}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
      </div>

      {isOpen && (
        <div className={`absolute top-full left-0 right-0 mt-1 rounded-xl border shadow-xl z-50 overflow-hidden ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        }`}>
          {languages.map((l) => (
            <div
              key={l.code}
              className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
                isDarkMode
                  ? l.code === lang ? 'bg-orange-900/30 text-orange-300' : 'hover:bg-gray-700 text-gray-200'
                  : l.code === lang ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50 text-gray-700'
              }`}
              onClick={() => changeLanguage(l.code)}
            >
              <img src={l.flag} alt="" className="w-4 h-3" />
              <span className="text-sm font-medium">{l.name}</span>
              {l.code === lang && <span className="ml-auto text-xs opacity-60">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
